import {DOMOperator} from "../../../util/DOMOperator.js";
import * as Chart from '../../../lib/@toast-ui/toastui-chart.min.js';
import {EmptyComponent} from "./EmptyComponent.js";
import {convertMinutes, getYaxisByMinutes} from "../../insight/util/util.js";

const chart = window.toastui?.Chart || Chart;

export class ChartComponent extends DOMOperator {
    constructor(widgetData) {
        super(`<div id="chart-area" style="width: 100%; height: 92%;"></div>`);
        widgetData && this.setItems(widgetData);
    }

    setItems(widgetData) {
        const {data, options} = this.convertData(widgetData);
        if (this.isChartDataEmpty(data.series)) {
            const emptyComponent = new EmptyComponent({
                emptyClass: widgetData.EMPTY_WIDGET_STYLE,
                emptyMsg: i18next.t(main.widget.noInfo),
                defaultClass : widgetData.defaultClass
            });
            this.getElement().innerHtml = "";
            this.attach(emptyComponent);
            return;
        }
        const {CHART_TYPE} = widgetData;
        const widgetParam = {
            el: this.getElement(),
            data,
            options
        }
        switch (CHART_TYPE) {
            case 'area':
                chart.areaChart(widgetParam);
                break;
            case 'line':
                chart.lineChart(widgetParam);
                break;
            case 'column':
                chart.columnChart(widgetParam);
                break;
        }

        this.setDataSize(data.categories.length);
    }

    setDataSize(size) {
        this.getElement().setAttribute("data-lenth", size);
    }

    isChartDataEmpty(arr) {
        return arr.every(obj => obj.data.flatMap(val => val).every(value => value === 0));
    }

    convertData(widgetData) {
        const {items, CHART_OPTION, INSIGHT_TEMPLATE_SRNO} = widgetData;
        let categories = [];
        let series = [];
        const mapStatusData = (items) => {
            return [
                {name: i18next.t(dictionary.request), data: items.map(item => +item.REQUEST)},
                {name: i18next.t(dictionary.progress), data: items.map(item => +item.PROGRESS)},
                {name: i18next.t(dictionary.feedback), data: items.map(item => +item.FEEDBACK)},
                {name: i18next.t(dictionary.complete), data: items.map(item => +item.COMPLETE)},
                {name: i18next.t(dictionary.hold), data: items.map(item => +item.HOLD)},
            ]
        };

        switch (+INSIGHT_TEMPLATE_SRNO) {
            case 1: // 담당자별 업무
                categories = items.map(item => item.NAME ? item.NAME : "담당자 없음" );
                series = mapStatusData(items);
                CHART_OPTION.xAxis = this.convertXaxisLength(CHART_OPTION.xAxis);
                break;
            case 2: // 주차별 업무 건수
                categories = items.map(item => `${item.YEAR}/${item.MONTH}/${item.DAY}/${+item.WEEK}`);
                series = [{name: i18next.t(common.numOfTask), data: items.map(({REMAIN_COUNT}) => +REMAIN_COUNT)}];
                CHART_OPTION.xAxis = this.convertWeekCount(CHART_OPTION.xAxis);
                CHART_OPTION.tooltip = this.setCustomTooltip();
                CHART_OPTION.series = this.setZoomalbe(CHART_OPTION.series);
                break;
            case 7: // 주차별 업무 증가 추이
                categories = items.map(item => `${item.YEAR}/${item.MONTH}/${item.DAY}/${+item.WEEK}`);
                series = [
                    {
                        name: 'Request',
                        data: items.map(({REQ_COUNT}) => +REQ_COUNT),
                    },
                    {
                        name: 'Done',
                        data: items.map(({DONE_COUNT}) => +DONE_COUNT),
                    },
                ];
                CHART_OPTION.xAxis = this.convertWeekValue(CHART_OPTION.xAxis);
                CHART_OPTION.tooltip = this.setCustomTooltip();
                break;
            case 8: // 그룹별 업무
                categories = items.map(item => item.SECTION_NAME);
                series = mapStatusData(items);
                CHART_OPTION.xAxis = this.convertXaxisLength(CHART_OPTION.xAxis);
                break;
            case 9: // 담당자별 평균 업무 처리 속도
                categories = items.map(item => item.NAME);
                series = [
                    {name: '평균 시간', data: items.map(item => +item.TOTAL_MINUTES)}
                ];
                CHART_OPTION.xAxis = this.convertXaxisLength(CHART_OPTION.xAxis);
                CHART_OPTION.tooltip = {
                    template: (model, defaultTooltipTemplate, theme) => {
                        return `<div class="toastui-chart-tooltip toastui-chart-custom-tooltip">
                                    <p class="text-bold">${model.category}</p>
                                    <p>${model.data[0].label} ${convertMinutes(model.data[0].value)}</p>
                                  </div>`;
                    }
                };
                const maxTotalMinutes = Math.max(...items.map(item => +item.TOTAL_MINUTES));
                CHART_OPTION.yAxis = {
                    label: {
                        formatter: getYaxisByMinutes(maxTotalMinutes),
                    }
                }
                break;
            case 10: // 담당자별 마감일 지난 업무 건수
                categories = items.map(item => item.NAME);
                series = [
                    {name: '업무 건수', data: items.map(item => +item.COUNT)}
                ];
                CHART_OPTION.xAxis = this.convertXaxisLength(CHART_OPTION.xAxis);
                break;
        }

        return {options: CHART_OPTION, data: {categories, series}}
    }

    convertWeekValue(xAxisOption) {
        return {
            ...xAxisOption,
            label: {
                formatter: (value, axisLabelInfo) => {
                    const dateInfo = this.getDateInfo(value);
                    return `${dateInfo.MONTH}/${dateInfo.DAY}`;
                },
            }
        }
    }
    convertWeekCount(xAxisOption) {
        return {
            ...xAxisOption,
            label: {
                formatter: (value, axisLabelInfo) => {
                    const dateInfo = this.getDateInfo(value);
                    return `${i18next.t(date.byWeek, {val: dateInfo.WEEK})}`;
                },
            }
        }
    }

    setCustomTooltip() {
        return {
            template: (model, defaultTooltipTemplate, theme) => {
                const {category, color, value} = model?.data[0];
                const {body, header} = defaultTooltipTemplate;
                const {background} = theme;
                const dateInfo = this.getDateInfo(model.category);
                const startDate = dayjs(`${dateInfo.YEAR}/${dateInfo.MONTH}/${dateInfo.DAY}`)

                return `<div class="custom-tooltip-div-tag" style="background: ${background};">
                            <p class="custom-tooltip-p-tag">
                                ${i18next.t(common.weekOfYear, {val:dateInfo.YEAR, count:dateInfo.WEEK})}<br>
                                ${startDate.format('MM/DD')} ~ ${startDate.add('6','day').format('MM/DD')}
                            </p>
                            ${body}
                      </div>`;
            }
        };
    }

    setZoomalbe(series){
        return {
            ...series,
            zoomable: true
        }
    }
    convertXaxisLength(xAxisOption) {
        return {
            ...xAxisOption,
            label: {
                fontSize: 5,
                rotatable: false,
                formatter: (value) => {
                    if (6 <= value.length) return `${value.slice(0, 6)}...`
                    return `${value}`;
                }
            },
        }
    }

    getDateInfo(arr) {
        const dateInfo = arr.split('/');
        return {
            YEAR: dateInfo[0],
            MONTH: dateInfo[1],
            DAY: dateInfo[2],
            WEEK: dateInfo[3]
        }
    }
}
